import { useMemo } from "react";
import {
  Case,
  CategoryKey,
  QuickFilterKey,
  SortMode,
  QuickFilterConfig,
} from "../types/home";
import {
  CATEGORIES,
  filterAndSortCases,
  groupCasesByCategory,
  getAvailableFilters,
} from "../utils/homeCaseGrouping";

// ============================================
// USE HOME CASES CONTROLLER
// ============================================

export interface UseHomeCasesControllerInput {
  cases: Case[];
  searchQuery: string;
  quickFilter: QuickFilterKey;
  sortMode: SortMode;
  expandedCategories: Record<CategoryKey, boolean>;
}

export interface UseHomeCasesControllerOutput {
  // Filtered and sorted cases
  filteredCases: Case[];
  // Grouped by category
  groupedCases: Record<CategoryKey, Case[]>;
  // Categories that have visible cases
  nonEmptyCategories: typeof CATEGORIES;
  // Available quick filters (based on data)
  availableFilters: QuickFilterConfig[];
  // Counts for display
  totalCasesCount: number;
  filteredCasesCount: number;
  nonEmptyCategoriesCount: number;
  // Whether all non-empty categories are expanded
  allExpanded: boolean;
  // Whether there are any results after filtering
  hasResults: boolean;
  // Whether search/filter is active
  isFiltering: boolean;
}

export function useHomeCasesController({
  cases,
  searchQuery,
  quickFilter,
  sortMode,
  expandedCategories,
}: UseHomeCasesControllerInput): UseHomeCasesControllerOutput {
  // Determine available quick filters based on case data
  const availableFilters = useMemo(
    () => getAvailableFilters(cases),
    [cases]
  );

  // Filter and sort cases
  const filteredCases = useMemo(() => {
    return filterAndSortCases(cases, {
      query: searchQuery,
      quickFilter,
      sortMode,
    });
  }, [cases, searchQuery, quickFilter, sortMode]);

  // Group filtered cases by category
  const groupedCases = useMemo(
    () => groupCasesByCategory(filteredCases),
    [filteredCases]
  );

  // Get non-empty categories
  const nonEmptyCategories = useMemo(
    () => CATEGORIES.filter((cat) => groupedCases[cat.key].length > 0),
    [groupedCases]
  );

  // Check if all non-empty categories are expanded
  const allExpanded = useMemo(
    () => nonEmptyCategories.every((cat) => expandedCategories[cat.key]),
    [nonEmptyCategories, expandedCategories]
  );

  // Determine if filtering is active
  const isFiltering = searchQuery.trim().length > 0 || quickFilter !== "all";

  return {
    filteredCases,
    groupedCases,
    nonEmptyCategories,
    availableFilters,
    totalCasesCount: cases.length,
    filteredCasesCount: filteredCases.length,
    nonEmptyCategoriesCount: nonEmptyCategories.length,
    allExpanded,
    hasResults: filteredCases.length > 0,
    isFiltering,
  };
}